import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ModalDragHandleProps } from '../../hooks/useModalDrag';

export interface ModalHeaderProps {
  title: React.ReactNode;
  onClose?: () => void;
  /** From useModalDrag — makes the bar a drag handle. */
  dragHandleProps?: ModalDragHandleProps;
  /** Extra controls rendered to the left of the close button. */
  actions?: React.ReactNode;
  /** Appended to the bar's classes (e.g. to change rounding). */
  className?: string;
  /** id for the <h2>, for aria-labelledby wiring. */
  titleId?: string;
  closeLabel?: string;
}

/**
 * The app-standard modal header: a dark bar, white title, close button, and
 * (when dragHandleProps is passed) the drag handle for moving the modal.
 *
 * Colors come from the --cmp-modal-header-* tokens so the Customize page can
 * restyle every modal in the app - this one component is rendered by 114 files.
 * The tokens default to the slate-700 bar it has always had, in both modes: it
 * carries no `dark:` variants, and the dark token deliberately matches the light
 * one so tokenizing changed nothing.
 */
export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  onClose,
  dragHandleProps,
  actions,
  className = '',
  titleId,
  closeLabel = 'Close',
}) => (
  <div
    {...dragHandleProps}
    className={`flex shrink-0 items-center justify-between gap-3 rounded-t-lg border-b border-cmp-modal-header-edge bg-cmp-modal-header px-4 py-3 sm:px-6 text-cmp-modal-header-fg ${
      dragHandleProps ? 'cursor-move select-none touch-none' : ''
    } ${className}`}
  >
    <h2
      id={titleId}
      className="min-w-0 truncate cursor-text select-text text-base font-semibold text-cmp-modal-header-fg"
      // Keep native text selection without letting the header capture the pointer.
      onPointerDown={(event) => event.stopPropagation()}
    >
      {title}
    </h2>
    <div className="ml-3 flex shrink-0 items-center gap-2">
      {actions}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="relative rounded-md text-cmp-modal-header-muted transition-colors hover:text-cmp-modal-header-fg focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <span className="absolute -inset-2.5"></span>
          <span className="sr-only">{closeLabel}</span>
          <XMarkIcon className="size-6" aria-hidden="true" />
        </button>
      )}
    </div>
  </div>
);

export default ModalHeader;
